<template>
  <f7-block-title>{{ ctaLabel }}</f7-block-title>
  <f7-list inset strong>
    <f7-list-input
      v-if="requiresQuantity"
      :label="$t('campaign.quantityOrPlaces')"
      type="number"
      :value="quantity"
      @input="quantity = $event.target.value"
    />
  </f7-list>
  <f7-block>
    <f7-button large round raised fill @click="submit">{{ ctaLabel }}</f7-button>
  </f7-block>
  <f7-block v-if="ticketCode" class="ticket-block">
    <f7-icon f7="ticket_fill" size="18" />
    <div>
      <div class="ticket-code">{{ ticketCode }}</div>
      <div class="ticket-hint">{{ $t('campaign.showCodeAtEntry') }}</div>
    </div>
  </f7-block>
</template>

<script setup>
import { ref, computed } from 'vue'
import { f7 } from 'framework7-vue'
import client from '../../js/api/client.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import i18n from '../../js/i18n.js'

const { t } = i18n.global

const props = defineProps({
  campaignId: { type: String, required: true },
  campaignType: { type: String, required: true },
})
const emit = defineEmits(['registered'])

const keycloak = useKeycloakStore()
const quantity = ref(1)
const ticketCode = ref(null)

const requiresQuantity = computed(() =>
  ['TICKETING', 'RAFFLE'].includes(props.campaignType))

const ctaLabel = computed(() => ({
  TICKETING:          t('campaign.ctaTicketing'),
  EVENT_REGISTRATION: t('campaign.ctaEventRegistration'),
  RSVP:               t('campaign.ctaRsvp'),
  RAFFLE:             t('campaign.ctaRaffle'),
  MEMBERSHIP:         t('campaign.ctaMembership'),
  SUBSCRIPTION:       t('campaign.ctaSubscription'),
  TONTINE:            t('campaign.ctaTontine'),
  AUCTION:            t('campaign.ctaAuction'),
}[props.campaignType] || t('campaign.ctaParticipate')))

async function submit() {
  f7.preloader.show()
  try {
    const { data } = await client.post(
      `/campaigns/${props.campaignId}/participants`, {
        userId: keycloak.subject,
        quantity: Number(quantity.value),
      })
    f7.preloader.hide()
    ticketCode.value = data.ticketCode
    emit('registered', data)
    f7.toast.show({
      text: data.status === 'WAITLISTED' ? t('campaign.waitlisted') : t('campaign.registrationConfirmed'),
      closeTimeout: 2000,
    })
  } catch (e) {
    f7.preloader.hide()
    f7.dialog.alert(e?.response?.data?.message || t('campaign.registrationFailed'))
  }
}
</script>

<style scoped>
.ticket-block {
  display: flex; align-items: center; gap: 10px;
  background: color-mix(in srgb, var(--f7-theme-color) 8%, transparent);
  border-radius: 10px; padding: 12px;
}
.ticket-code { font-size: 16px; font-weight: 500; font-family: var(--f7-font-mono, monospace); }
.ticket-hint { font-size: 11px; color: var(--f7-on-surface-variant, #8e8e93); }
</style>
